import {
  getPeripheralStrengthInterpretationText,
  getPeripheralStrengthSeverityLabel,
  getPeripheralStrengthSeverityTone,
  normalizePeripheralMuscleStrengthPayload,
  summarizePeripheralMuscleStrengthResult,
  type PeripheralMuscleStrengthResultPayload,
} from '../peripheral-muscle/peripheralMuscleDomain';

export {
  getPeripheralStrengthInterpretationText,
  getPeripheralStrengthSeverityLabel,
  getPeripheralStrengthSeverityTone,
};

interface PeripheralMuscleStrengthResultRendererProps {
  payload: PeripheralMuscleStrengthResultPayload | null | undefined;
}

export default function PeripheralMuscleStrengthResultRenderer({
  payload,
}: PeripheralMuscleStrengthResultRendererProps) {
  const normalized = normalizePeripheralMuscleStrengthPayload(payload);
  const summary = summarizePeripheralMuscleStrengthResult(payload);

  if (!normalized) {
    return (
      <div className="rounded-[24px] border border-slate-100 bg-white p-6 shadow-sm">
        <div className="text-sm text-slate-500">Dados da força muscular periférica indisponíveis para este resultado.</div>
      </div>
    );
  }

  const severityLabel = getPeripheralStrengthSeverityLabel(normalized);
  const severityTone = getPeripheralStrengthSeverityTone(normalized);
  const interpretation = getPeripheralStrengthInterpretationText(normalized);

  return (
    <div className="space-y-6">
      <div className="grid gap-6 md:grid-cols-5">
        <div className="rounded-[28px] border border-slate-200 bg-white p-8 text-center shadow-sm md:col-span-2">
          <p className="mb-4 text-xs font-black uppercase tracking-[0.25em] text-primary-700">Força muscular periférica</p>
          <div className="text-6xl font-black leading-none text-primary-800">
            {summary.lowestPercentPredicted != null ? `${summary.lowestPercentPredicted.toFixed(0)}%` : '—'}
          </div>
          <div className="mt-2 text-sm font-semibold text-slate-700">
            {summary.lowestMuscleLabel ? `comparação exploratória em ${summary.lowestMuscleLabel.toLowerCase()}` : 'sem comparação normativa'}
          </div>
          <div className={`mt-4 inline-flex rounded-full px-4 py-1.5 text-xs font-bold shadow-sm ${severityTone}`}>{severityLabel}</div>
        </div>

        <div className="flex flex-col justify-center space-y-4 md:col-span-3">
          <div>
            <h4 className="text-2xl font-black tracking-tight text-slate-800">Resumo clínico</h4>
            <p className="mt-3 max-w-3xl text-sm leading-relaxed text-slate-600">{interpretation}</p>
          </div>

          <div className="grid gap-3 text-sm text-slate-600 md:grid-cols-2">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
              Lado avaliado: <span className="font-semibold text-slate-900">{summary.dominantSideLabel}</span> • Sexo:{' '}
              <span className="font-semibold text-slate-900">{summary.sex === 'F' ? 'Feminino' : 'Masculino'}</span>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
              Idade: <span className="font-semibold text-slate-900">{summary.ageYears != null ? `${summary.ageYears} anos` : '—'}</span> • BMI:{' '}
              <span className="font-semibold text-slate-900">{summary.bmi != null ? `${summary.bmi.toFixed(2)} kg/m²` : '—'}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm leading-relaxed text-amber-900">
        Garcia et al. (2024): valores previstos exibidos em modo de validação. A unidade, o protocolo e os limites inferiores esperados ainda não são reproduzíveis; este resultado não classifica fraqueza muscular.
      </div>

      <div className="rounded-[24px] border border-slate-100 bg-white p-6 shadow-sm">
        <div className="text-[10px] font-black uppercase tracking-[0.24em] text-slate-400">Comparação por grupo muscular</div>
        <div className="mt-4 overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead>
              <tr className="text-left text-xs font-black uppercase tracking-[0.18em] text-slate-400">
                <th className="pb-3 pr-4">Músculo</th>
                <th className="pb-3 pr-4">Melhor</th>
                <th className="pb-3 pr-4">Predito</th>
                <th className="pb-3 pr-4">% predito</th>
                <th className="pb-3 pr-4">Top 2</th>
                <th className="pb-3">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-slate-700">
              {summary.muscles.map((muscle) => (
                <tr key={muscle.key}>
                  <td className="py-3 pr-4">
                    <div className="font-semibold text-slate-900">{muscle.label}</div>
                    <div className="text-xs text-slate-500">{muscle.equationLabel}</div>
                  </td>
                  <td className="py-3 pr-4">{muscle.bestKgf != null ? `${muscle.bestKgf.toFixed(1)} kgf` : '—'}</td>
                  <td className="py-3 pr-4">{muscle.predictedKgf != null ? `${muscle.predictedKgf.toFixed(1)} kgf` : '—'}</td>
                  <td className="py-3 pr-4">{muscle.percentPredicted != null ? `${muscle.percentPredicted.toFixed(1)}%` : '—'}</td>
                  <td className="py-3 pr-4">{muscle.topTwoDiffPercent != null ? `${muscle.topTwoDiffPercent.toFixed(1)}%` : '—'}</td>
                  <td className="py-3">
                    <span
                      className={`inline-flex rounded-full px-3 py-1 text-xs font-bold ${
                        !muscle.completed
                          ? 'bg-amber-100 text-amber-700'
                          : muscle.reproducible
                            ? 'bg-emerald-100 text-emerald-700'
                            : 'bg-amber-100 text-amber-700'
                      }`}
                    >
                      {!muscle.completed ? 'Incompleto' : muscle.reproducible ? 'Reprodutível' : 'Variabilidade >5%'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        {summary.muscles.map((muscle) => (
          <div key={`attempts-${muscle.key}`} className="rounded-[24px] border border-slate-100 bg-slate-50 p-5 shadow-sm">
            <div className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">{muscle.label}</div>
            <div className="mt-3 text-sm text-slate-700">
              {muscle.attemptsKgf.length
                ? muscle.attemptsKgf.map((value) => `${value.toFixed(1)} kgf`).join(' · ')
                : 'Nenhuma tentativa registrada'}
            </div>
          </div>
        ))}
      </div>

      <div className="rounded-[24px] border border-slate-100 bg-white p-6 shadow-sm">
        <div className="text-[10px] font-black uppercase tracking-[0.24em] text-slate-400">Protocolo aplicado</div>
        <div className="mt-4 grid gap-3 text-sm text-slate-700 md:grid-cols-2">
          <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
            CIVM de {summary.protocol.contractionSeconds} s no membro dominante
          </div>
          <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
            Entre {summary.protocol.minAttempts} e {summary.protocol.maxAttempts} tentativas por músculo
          </div>
          <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
            Critério de qualidade: top 2 diferem menos de {summary.protocol.reproducibilityThresholdPercent}%
          </div>
          <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
            Equipamento de referência: {summary.protocol.supplier}
          </div>
        </div>
      </div>

      {summary.notes ? (
        <div className="rounded-[24px] border border-slate-100 bg-white p-6 shadow-sm">
          <div className="text-[10px] font-black uppercase tracking-[0.24em] text-slate-400">Observações</div>
          <p className="mt-3 text-sm leading-relaxed text-slate-600">{summary.notes}</p>
        </div>
      ) : null}
    </div>
  );
}
